<template>
  <div>
    <div v-if="showModal" class="modal fade show" style="display: block" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Phone Payment Calculator</h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted mb-4">
              Adjust how much you want to pay monthly for your phone. Your phone bundle discount will automatically 
              be applied to reduce your total monthly payment. Use the slider to see how adjusting your monthly 
              phone payment affects your total monthly cost.
            </p>
            <div class="formsec">
              <!-- Phone Selection -->
              <div class="formRow">
                <label>{{ $t('select_phone_label') }}
                  <i class="fas fa-question-circle"
                     v-tooltip="'Select a phone to calculate monthly payments'"
                  />
                </label>
                <select v-model="selectedPhoneId" class="form-control" @change="handlePhoneChange">
                  <option v-for="(phone, index) in phones"
                          :key="index"
                          :value="index + 1">
                    {{ phone.phone }} - List Price: {{ formatMoney(phone.rogers_list) }}
                  </option>
                </select>
              </div>

              <!-- Color Selection -->
              <div v-if="selectedPhoneId" class="formRow">
                <label>{{ $t('select_color') }}</label>
                <select required id="phone_color" v-model="selectedColor">
                  <option value="">{{ $t('select_phone_color') }}</option>
                  <option v-for="(color, index) in phones[selectedPhoneId-1].colours" 
                          :key="index">
                    {{ color }}
                  </option>
                </select>
              </div>

              <!-- Monthly Phone Payment -->
              <div class="formRow">
                <label>Monthly Phone Payment
                  <i class="fas fa-question-circle"
                     v-tooltip="'Base monthly payment for the phone before any discounts'"
                  />
                </label>
                <div class="input-group">
                  <input type="text"
                         class="form-control"
                         :value="monthlyPaymentInput"
                         @input="handleMonthlyInput"
                         @blur="validateMonthlyInput">
                  <span class="input-group-text">+ tax</span>
                </div>
              </div>

              <!-- Total Monthly Payment -->
              <div class="formRow">
                <label>Total Monthly Payment
                  <i class="fas fa-question-circle"
                     v-tooltip="'Total monthly payment including plan and phone payment after discount'"
                  />
                </label>
                <div class="input-group">
                  <input type="text"
                         class="form-control"
                         :value="formatMoney(totalMonthlyPayment)"
                         readonly>
                  <span class="input-group-text">+ tax</span>
                </div>
              </div>

              <!-- List Price -->
              <div class="formRow">
                <label>Phone List Price</label>
                <div class="input-group">
                  <input type="text"
                         class="form-control"
                         :value="formatMoney(listPrice)"
                         readonly>
                  <span class="input-group-text">+ tax</span>
                </div>
              </div>

              <!-- Upfront Payment -->
              <div class="formRow">
                <label>Upfront Payment
                  <i class="fas fa-question-circle"
                     v-tooltip="'Initial payment required for the phone'"
                  />
                </label>
                <div class="input-group">
                  <input type="text"
                         class="form-control"
                         :value="upfrontPaymentInput"
                         @input="handleUpfrontInput"
                         @blur="validateUpfrontInput">
                  <span class="input-group-text">+ tax</span>
                </div>
              </div>

              <!-- Payment Adjustment Slider -->
              <div class="formRow">
                <label>Adjust Monthly Phone Payment
                  <i class="fas fa-question-circle"
                     v-tooltip="'Slide to adjust monthly phone payment. This will automatically update the upfront payment.'"
                  />
                </label>
                <input type="range"
                       v-model.number="sliderValue"
                       :min="0"
                       :max="maxMonthlyPayment"
                       :step="0.01"
                       class="form-range"
                       @input="handleSliderChange">
                <div class="range-values">
                  <small>Min: $0.00</small>
                  <small>Max: {{ formatMoney(maxMonthlyPayment) }}</small>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button"
                    class="btn btn-secondary"
                    @click="closeModal">Cancel</button>
            <button type="button"
                    class="btn btn-primary"
                    @click="saveChanges"
                    :disabled="!isValid">Save changes</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Feedback Toast -->
    <div v-if="showFeedback"
         class="toast show"
         style="position: fixed; top: 20px; right: 20px;">
      <div class="toast-header">
        <strong class="me-auto">{{ feedbackTitle }}</strong>
        <button type="button" class="btn-close" @click="closeFeedback"></button>
      </div>
      <div class="toast-body">
        {{ feedbackMessage }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PhonePaymentCalculator',

  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    initialPhoneId: {
      type: Number,
      default: 0
    },
    phoneColor: {
      type: String,
      default: ''
    },
    phones: {
      type: Array,
      required: true
    },
    currentPlan: {
      type: Object,
      required: true
    },
    form: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      selectedPhoneId: this.initialPhoneId,
      selectedColor: this.phoneColor,
      sliderValue: 0,
      showFeedback: false,
      feedbackTitle: '',
      feedbackMessage: '',
      monthlyPaymentInput: '0.00',
      upfrontPaymentInput: '0.00'
    };
  },

  computed: {
    selectedPhone() {
      if (!this.selectedPhoneId) return null;
      return this.phones[this.selectedPhoneId - 1];
    },

    phoneDiscount() {
      if (!this.selectedPhone || !this.currentPlan) return 0;
      
      const planData = this.currentPlan.data;
      switch (planData) {
        case "10GB": return 3.75;
        case "25GB": return 2.65;
        case "50GB": return 2.25;
        case "100GB": return 2.25;
        default: return 0;
      }
    },

    listPrice() {
      if (!this.selectedPhone) return 0;
      return this.selectedPhone.rogers_list;
    },

    baseMonthlyPayment() {
      return +this.sliderValue;
    },

    effectiveMonthlyPayment() {
      return this.baseMonthlyPayment;
    },

    totalMonthlyPayment() {
      if (!this.currentPlan || !this.selectedPhone) return this.effectiveMonthlyPayment;
      const planRate = parseFloat(this.currentPlan.BYOD) || 0;
      return +(planRate + this.effectiveMonthlyPayment).toFixed(2);
    },

    upfrontPayment() {
      if (!this.selectedPhone) return 0;
      if (this.baseMonthlyPayment >= this.maxMonthlyPayment) return 0;
      const monthlyPaymentWithDiscount = this.baseMonthlyPayment + this.phoneDiscount;
      const remainingCost = this.listPrice - (monthlyPaymentWithDiscount * 36);
      return Math.max(0, +remainingCost.toFixed(2));
    },

    maxMonthlyPayment() {
      if (!this.selectedPhone) return 0;
      const maxBase = +(this.listPrice / 36).toFixed(2);
      return +(maxBase - this.phoneDiscount).toFixed(2);
    },

    isValid() {
      return this.selectedPhoneId && 
             this.selectedColor && 
             this.baseMonthlyPayment >= 0 && 
             this.baseMonthlyPayment <= this.maxMonthlyPayment;
    }
  },

  methods: {
    handlePhoneChange() {
      if (this.selectedPhone) {
        this.sliderValue = 0;
        this.updateInputValues();
      }
    },

    handleMonthlyInput(event) {
      let value = event.target.value.replace(/[^0-9.]/g, '');
      this.monthlyPaymentInput = value;
      
      if (!isNaN(value) && value !== '') {
        const numValue = parseFloat(value);
        if (numValue >= 0 && numValue <= this.maxMonthlyPayment) {
          this.sliderValue = +numValue.toFixed(2);
          this.upfrontPaymentInput = this.formatMoney(this.upfrontPayment);
        }
      }
    },

    handleUpfrontInput(event) {
      let value = event.target.value.replace(/[^0-9.]/g, '');
      this.upfrontPaymentInput = value;
      
      if (!isNaN(value) && value !== '') {
        const numValue = parseFloat(value);
        if (numValue >= 0 && numValue <= this.listPrice) {
          // Calculate new monthly payment considering the phone discount
          const totalMonthlyAmount = +((this.listPrice - numValue) / 36).toFixed(2);
          const newMonthlyPayment = +(totalMonthlyAmount - this.phoneDiscount).toFixed(2);
          
          if (newMonthlyPayment >= 0 && newMonthlyPayment <= this.maxMonthlyPayment) {
            this.sliderValue = newMonthlyPayment;
            this.monthlyPaymentInput = this.formatMoney(newMonthlyPayment);
          }
        }
      }
    },

    handleSliderChange() {
      this.updateInputValues();
    },

    updateInputValues() {
      this.monthlyPaymentInput = this.formatMoney(this.baseMonthlyPayment);
      this.upfrontPaymentInput = this.formatMoney(this.upfrontPayment);
    },

    validateMonthlyInput() {
      this.monthlyPaymentInput = this.formatMoney(this.baseMonthlyPayment);
    },

    validateUpfrontInput() {
      this.upfrontPaymentInput = this.formatMoney(this.upfrontPayment);
    },

    saveChanges() {
      if (!this.isValid) {
        this.showFeedbackMessage('Error', 'Please complete all required fields');
        return;
      }

      this.$emit('save', {
        phoneId: this.selectedPhoneId,
        color: this.selectedColor,
        monthlyPayment: this.baseMonthlyPayment,
        upfrontPayment: this.upfrontPayment,
        phoneDisc: this.phoneDiscount
      });

      this.showFeedbackMessage('Success', 'Payment settings saved');
    },

    showFeedbackMessage(title, message) {
      this.feedbackTitle = title;
      this.feedbackMessage = message;
      this.showFeedback = true;
      setTimeout(() => this.showFeedback = false, 3000);
    },

    closeModal() {
      this.$emit('close');
    },

    closeFeedback() {
      this.showFeedback = false;
    },

    formatMoney(value) {
      return `${Number(value).toFixed(2)}`;
    }
  },

  watch: {
    selectedPhoneId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.handlePhoneChange();
        }
      }
    }
  }
};
</script>

<style scoped>
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

.range-values {
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
}

.toast {
  z-index: 1100;
}

.formRow {
  margin-bottom: 1rem;
}

.formRow label {
  display: block;
  margin-bottom: 0.5rem;
}

.form-range {
  width: 100%;
}

[v-tooltip] {
  cursor: help;
  margin-left: 5px;
}
</style>
